import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import { EventType } from 'chromecast-caf-receiver/cast.framework.events';
import {
  MessageType,
  StreamType,
  TextTrackStyle,
} from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import type { CastMedia } from '../types/media';
import { Hermes } from '../network';
import { Logger } from '../logger';
import { getLanguageLabel } from '../language-tags';

/**
 * Tracks handler — manages audio and subtitle track selection.
 *
 * Ported from `legacy/js/receiver.js` (lines 408-508). Responsible for:
 * - Mapping available audio and subtitle tracks after `PLAYER_LOAD_COMPLETE`
 * - Applying user language preferences (`userAudio`, `userSub`)
 * - Handling forced Portuguese subtitles (track `pt`)
 * - Applying `TextTrackStyle` (CircularMedium font, 1.05x scale)
 * - Exposing available tracks via `getAvailableLanguageTracks()` for `MEDIA_STATUS`
 *
 * **Differences from legacy:**
 * - Fully typed with TypeScript — `TrackInfo` and `AvailableLanguageTracks` interfaces
 * - Uses Hermes SDK (`UpdateUserLanguageSettings`) instead of XHR for persistence
 * - Uses `getLanguageLabel()` from `language-tags.ts` instead of inline RFC5646 map
 * - Logging via `Logger` with structured data
 */

const LOG_TAG = 'TRACKS';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single track entry exposed to the sender UI. */
export interface TrackInfo {
  trackId: number;
  lang: string;
  label: string;
}

/** Audio and subtitle track lists sent via `MEDIA_STATUS` customData. */
export interface AvailableLanguageTracks {
  audio: TrackInfo[];
  subtitle: TrackInfo[];
}

// ---------------------------------------------------------------------------
// Core: enumerate and select tracks
// ---------------------------------------------------------------------------

/**
 * Enumerates available audio/subtitle tracks and applies user preferences.
 *
 * Called on `PLAYER_LOAD_COMPLETE`. Algorithm:
 * 1. Clear `castMedia.audios` and `castMedia.subtitles` Maps
 * 2. Enumerate audio tracks — apply `userAudio` preference
 * 3. Enumerate subtitle tracks — handle forced `pt` track separately
 * 4. Apply `TextTrackStyle` with CircularMedium font
 */
function languageTracksHandler(
  playerManager: PlayerManager,
  session: CastSession,
  media: CastMedia,
): void {
  // -- Audio tracks --
  media.audios.clear();
  const audioTracksManager = playerManager.getAudioTracksManager();
  const audioTracks = audioTracksManager.getTracks();

  if (audioTracks.length > 0) {
    for (const track of audioTracks) {
      const lang = track.language ?? 'und';
      media.audios.set(track.trackId, lang);

      if (session.userAudio === 'original') {
        if (track.roles?.includes('main')) {
          audioTracksManager.setActiveByLanguage(lang);
        }
      } else if (lang.toLowerCase() === session.userAudio) {
        audioTracksManager.setActiveByLanguage(lang);
      }
    }

    Logger.debug(LOG_TAG, 'Audio tracks mapped', {
      count: audioTracks.length,
      userAudio: session.userAudio,
    });
  }

  // -- Subtitle / text tracks --
  media.subtitles.clear();
  media.hasForcedSubtitles = false;
  media.forcedSubtitle = null;

  const textTracksManager = playerManager.getTextTracksManager();
  const textTracks = textTracksManager.getTracks();

  if (textTracks.length > 0) {
    for (const track of textTracks) {
      const lang = track.language ?? 'und';

      if (lang === 'pt') {
        // Forced Portuguese subtitle — stored separately
        media.hasForcedSubtitles = true;
        media.forcedSubtitle = {
          trackId: track.trackId,
          lang: 'none',
          label: 'Desligado',
        };

        if (session.userSub === 'none') {
          textTracksManager.setActiveByLanguage(lang);
        }
      } else {
        media.subtitles.set(track.trackId, lang);

        if (
          lang.toLowerCase() === session.userSub &&
          session.userSub !== 'none'
        ) {
          textTracksManager.setActiveByLanguage(lang);
        }
      }
    }

    Logger.debug(LOG_TAG, 'Subtitle tracks mapped', {
      count: textTracks.length,
      hasForcedSubtitles: media.hasForcedSubtitles,
      userSub: session.userSub,
    });
  }

  // -- TextTrackStyle --
  const textTrackStyle = new TextTrackStyle();
  textTrackStyle.fontFamily = 'CircularMedium';
  textTrackStyle.fontScale = 1.05;
  textTracksManager.setTextTrackStyle(textTrackStyle);
}

// ---------------------------------------------------------------------------
// Track list for MEDIA_STATUS
// ---------------------------------------------------------------------------

/**
 * Builds the available audio/subtitle track lists for sender UI.
 *
 * Returns an empty object for LIVE streams or when customData/mediaId are missing.
 * The returned object is set as `customData` on `MEDIA_STATUS` messages.
 */
export function getAvailableLanguageTracks(
  playerManager: PlayerManager,
  media: CastMedia,
): AvailableLanguageTracks | Record<string, never> {
  try {
    const audioTracksManager = playerManager.getAudioTracksManager();
    const mediaInfo = playerManager.getMediaInformation();

    if (!mediaInfo) return {};

    // Bypass for LIVE streams
    if (mediaInfo.streamType === StreamType.LIVE) return {};

    const metadata = mediaInfo.metadata as
      | {
          customData?: {
            mediaId?: string;
            playlistId?: string;
            playlistType?: string;
          };
        }
      | undefined;

    if (!metadata?.customData) return {};

    const { mediaId, playlistId, playlistType } = metadata.customData;
    if (!mediaId || !playlistId) return {};

    // -- Audio list --
    const audioList: TrackInfo[] = [];
    media.audios.forEach((lang, trackId) => {
      // Legacy workaround: single audio in non-bp-select content is labeled
      // "Português (Original)" because some BP content uses 'en' or 'und'
      // for the only Portuguese audio track.
      let label: string;
      if (playlistType !== 'bp-select' && media.audios.size <= 1) {
        label = `${getLanguageLabel('pt-BR')} (Original)`;
      } else {
        const track = audioTracksManager.getTrackById(trackId);
        const isMain = track?.roles?.includes('main') ?? false;
        label = getLanguageLabel(lang) + (isMain ? ' (Original)' : '');
      }

      audioList.push({ trackId, lang, label });
    });

    // -- Subtitle list (always starts with "Desligado" / Off) --
    const subtitleList: TrackInfo[] = [
      {
        trackId: media.hasForcedSubtitles
          ? (media.forcedSubtitle?.trackId ?? 0)
          : 0,
        lang: 'none',
        label: 'Desligado',
      },
    ];

    media.subtitles.forEach((lang, trackId) => {
      subtitleList.push({
        trackId,
        lang,
        label: getLanguageLabel(lang),
      });
    });

    return { audio: audioList, subtitle: subtitleList };
  } catch (err) {
    Logger.error(LOG_TAG, 'Failed to build available language tracks', err);
    return {};
  }
}

// ---------------------------------------------------------------------------
// Hermes persistence
// ---------------------------------------------------------------------------

/**
 * Saves updated language preferences to Hermes.
 * Updates the local session state and persists via `UpdateUserLanguageSettings`.
 */
function updateUserLanguageSettings(
  session: CastSession,
  audio: string | null,
  sub: string | null,
): void {
  if (audio !== null) session.userAudio = audio;
  if (sub !== null) session.userSub = sub;

  Hermes.client
    .UpdateUserLanguageSettings({
      input: {
        player: {
          language: {
            audio: session.userAudio.toLowerCase(),
            subtitles: session.userSub.toLowerCase(),
          },
        },
      },
    })
    .then(() => {
      Logger.debug(LOG_TAG, 'Language settings saved to Hermes', {
        audio: session.userAudio,
        sub: session.userSub,
      });
    })
    .catch((err: unknown) => {
      Logger.error(LOG_TAG, 'Failed to save language settings', err);
    });
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the tracks handler on the given player manager.
 *
 * Wires up:
 * - `PLAYER_LOAD_COMPLETE` → enumerate tracks and apply preferences
 *
 * Note: The `MEDIA_STATUS`, `EDIT_AUDIO_TRACKS`, and `EDIT_TRACKS_INFO`
 * interceptors are handled by `media-status.ts` (seção 4.6), which calls
 * `getAvailableLanguageTracks()` from this module.
 *
 * @param playerManager - Cast player manager instance
 * @param session       - Mutable session state (userAudio, userSub)
 * @param media         - Mutable media state (audios, subtitles, forcedSubtitle)
 */
export function registerTracksHandler(
  playerManager: PlayerManager,
  session: CastSession,
  media: CastMedia,
): void {
  // ---------------------------------------------------------------
  // PLAYER_LOAD_COMPLETE — enumerate tracks and apply preferences
  // ---------------------------------------------------------------
  playerManager.addEventListener(EventType.PLAYER_LOAD_COMPLETE, () => {
    Logger.info(LOG_TAG, 'PLAYER_LOAD_COMPLETE — handling tracks');
    languageTracksHandler(playerManager, session, media);
  });

  // ---------------------------------------------------------------
  // MEDIA_STATUS — expose available tracks to sender
  // ---------------------------------------------------------------
  playerManager.setMessageInterceptor(
    MessageType.MEDIA_STATUS,
    (request) => {
      (request as { customData?: unknown }).customData =
        getAvailableLanguageTracks(playerManager, media);
      return request;
    },
  );

  // ---------------------------------------------------------------
  // EDIT_AUDIO_TRACKS — user changes audio track from sender
  // ---------------------------------------------------------------
  playerManager.setMessageInterceptor(
    MessageType.EDIT_AUDIO_TRACKS,
    (request) => {
      const audioTracksManager = playerManager.getAudioTracksManager();
      const audioTracks = audioTracksManager.getTracks();
      const targetLanguage = (request as { language?: string }).language;

      if (audioTracks.length > 0 && targetLanguage) {
        for (const track of audioTracks) {
          if (track.language === targetLanguage) {
            audioTracksManager.setActiveByLanguage(targetLanguage);
            updateUserLanguageSettings(session, targetLanguage, null);

            Logger.info(LOG_TAG, 'Audio track changed', {
              language: targetLanguage,
              trackId: track.trackId,
            });
            break;
          }
        }
      }

      return request;
    },
  );

  // ---------------------------------------------------------------
  // EDIT_TRACKS_INFO — user changes subtitle track from sender
  // ---------------------------------------------------------------
  playerManager.setMessageInterceptor(
    MessageType.EDIT_TRACKS_INFO,
    (request) => {
      const textTracksManager = playerManager.getTextTracksManager();
      const targetLanguage = (request as { language?: string }).language;

      if (targetLanguage !== undefined) {
        if (targetLanguage !== 'none') {
          textTracksManager.setActiveByLanguage(targetLanguage);
        } else {
          // "Off" — activate forced Portuguese if present, otherwise no subs
          const forcedIds = media.hasForcedSubtitles && media.forcedSubtitle
            ? [media.forcedSubtitle.trackId]
            : [];
          textTracksManager.setActiveByIds(forcedIds);
        }

        updateUserLanguageSettings(session, null, targetLanguage);

        Logger.info(LOG_TAG, 'Subtitle track changed', {
          language: targetLanguage,
          hasForcedSubtitles: media.hasForcedSubtitles,
        });
      }

      return request;
    },
  );

  Logger.info(LOG_TAG, 'Tracks handler registered');
}
